import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * NoIndexRobots, adds <meta name="robots" content="noindex,follow"> to
 * utility / private / auth / legal pages that should NOT be indexed by Google
 * but SHOULD still be linked-from (follow) so that internal page-rank flows.
 *
 * Why a component (and not a per-page Helmet entry)?
 *   - 30+ utility routes share this rule. A central matcher is DRY.
 *   - It also handles dynamic routes (/track/:ticketId, /ref/:code).
 *
 * The route list mirrors robots.txt Disallow rules, so search engines that
 * still try to crawl (some ignore robots.txt) will see noindex at the HTML
 * level too. Defense in depth.
 *
 * NOTE: This does NOT override existing Helmet noindex tags. It also does NOT
 * touch pages that should be indexed.
 */

const NOINDEX_PATTERNS = [
  // Auth / account
  /^\/login(\/|$)/,
  /^\/register(\/|$)/,
  /^\/reset-password(\/|$)/,
  /^\/verify-email(\/|$)/,
  /^\/my-account(\/|$)/,
  /^\/profile(\/|$)/,
  /^\/customer\/login(\/|$)/,
  /^\/customer\/portal(\/|$)/,
  // Admin / staff
  /^\/admin21(\/|$)/,
  /^\/employee-portal(\/|$)/,
  /^\/2aestimator(\/|$)/,
  // Order / tracking flows
  /^\/track(\/|$)/,
  /^\/track-repair(\/|$)/,
  /^\/track-sale(\/|$)/,
  /^\/check-warranty(\/|$)/,
  /^\/warranty-lookup(\/|$)/,
  /^\/counteroffer-response(\/|$)/,
  /^\/thank-you(\/|$)/,
  /^\/refurbished\/cart(\/|$)/,
  /^\/refurbished\/order-success(\/|$)/,
  // Legal & policy
  /^\/privacy(-policy)?(\/|$)/,
  /^\/terms(-and-conditions)?(\/|$)/,
  /^\/cookie-policy(\/|$)/,
  /^\/refund-policy(\/|$)/,
  /^\/accessibility(\/|$)/,
  /^\/sms-policy(\/|$)/,
  /^\/sms-terms(\/|$)/,
  /^\/user-agreement(\/|$)/,
  /^\/dsar(\/|$)/,
  /^\/law-enforcement(\/|$)/,
  // Referral codes
  /^\/ref\//,
];

const shouldNoIndex = (pathname) => NOINDEX_PATTERNS.some(rx => rx.test(pathname));

const NoIndexRobots = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const needsNoIndex = shouldNoIndex(path);

    // Find any existing 'robots' meta NOT managed by react-helmet
    let meta = document.querySelector('meta[name="robots"]:not([data-rh="true"])');

    if (needsNoIndex) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', 'noindex,follow');
      meta.setAttribute('data-noindex-robots', 'true');
    } else if (meta && meta.getAttribute('data-noindex-robots') === 'true') {
      // Remove our previously-set noindex when navigating to an indexable page
      meta.remove();
    }
  }, [location.pathname]);

  return null;
};

export default NoIndexRobots;
